import { countryDisplayName } from "@loot-os/api-contract";
import type { PackingSubStatus } from "@loot-os/fulfillment-core/order/packing-fold";
// Type-only import: the dashboard never bundles server code.
import type { AppRouter } from "@loot-os/functions/src/dashboard-api/trpc/router.js";
import type { inferOutput, TRPCOptionsProxy } from "@trpc/tanstack-react-query";

type OrdersProxy = TRPCOptionsProxy<AppRouter>["platform"]["fulfillment"]["orders"];

/** One row of the staff order list as it reaches the browser (dates as ISO strings). */
export type OrderListRow = inferOutput<OrdersProxy["search"]>["orders"][number];

/** One order on its detail page, with the list's derived fields. */
export type OrderDetail = inferOutput<OrdersProxy["get"]>;

/** Retool's own SubStatus words, so the two apps read the same side by side. */
const LABELS: Record<PackingSubStatus, string> = {
  READY_TO_SHIP: "READY TO SHIP",
  PARTIALLY_PACKED: "PARTIAL PACKED",
  NOT_PACKED: "NOT PACKED",
};

/** How far packing has got, in words; "" when nothing is left to pack. */
export function packingSubstatusLabel(substatus: PackingSubStatus | null): string {
  return substatus ? LABELS[substatus] : "";
}

/**
 * A stored order address as the lines a label prints, leaving out what it lacks. The order's
 * own copy is jsonb and may predate the address schema, so every field is checked, not assumed.
 */
export function orderAddressLines(address: unknown): string[] {
  if (typeof address !== "object" || address === null || Array.isArray(address)) return [];
  const fields = address as Record<string, unknown>;
  const read = (key: string) => {
    const value = fields[key];
    return typeof value === "string" ? value.trim() : "";
  };
  const country = read("country");
  return [
    [read("firstName"), read("lastName")].filter(Boolean).join(" "),
    read("addressLine1"),
    read("addressLine2"),
    [read("locality"), [read("region"), read("postalCode")].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", "),
    /^[A-Z]{2}$/.test(country) ? countryDisplayName(country) : country,
    read("phoneNumber"),
  ].filter(Boolean);
}

/** A status as Retool's tag column prints it (`_.startCase`): PRESHIPPED → "Preshipped". */
export function statusTagText(status: string): string {
  return status
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Tag colours, one per value so a status always reads the same (Retool colours its tags
 * automatically per value). The fulfillment pages follow the dashboard theme, so each tone has a
 * `dark:` value too.
 */
export const TAG_TONES = {
  blue: "bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300",
  amber: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300",
  violet: "bg-violet-100 text-violet-800 dark:bg-violet-500/15 dark:text-violet-300",
  green: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300",
  red: "bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-300",
  orange: "bg-orange-100 text-orange-800 dark:bg-orange-500/15 dark:text-orange-300",
  grey: "bg-slate-100 text-slate-700 dark:bg-slate-500/15 dark:text-slate-300",
} as const;

const ORDER_STATUS_TONE: Record<string, keyof typeof TAG_TONES> = {
  PENDING: "amber",
  PROCESSING: "blue",
  PRESHIPPED: "violet",
  SHIPPED: "green",
  SUCCESS: "green",
  CANCELLED: "red",
  FAIL: "red",
  HOLD: "orange",
  UNAPPROVED: "orange",
};

const PACKING_TONE: Record<PackingSubStatus, keyof typeof TAG_TONES> = {
  READY_TO_SHIP: "green",
  PARTIALLY_PACKED: "amber",
  NOT_PACKED: "grey",
};

const LINE_STATUS_TONE: Record<string, keyof typeof TAG_TONES> = {
  PENDING: "amber",
  PACKED: "green",
  CANCELLED: "red",
};

/** The tag classes for an order status; an unlisted one is grey. */
export function orderStatusTagClass(status: string): string {
  return TAG_TONES[ORDER_STATUS_TONE[status] ?? "grey"];
}

/** The tag classes for a packing substatus. */
export function packingTagClass(substatus: PackingSubStatus): string {
  return TAG_TONES[PACKING_TONE[substatus]];
}

/** The tag classes for an order line's status; an unlisted one is grey. */
export function lineStatusTagClass(status: string): string {
  return TAG_TONES[LINE_STATUS_TONE[status] ?? "grey"];
}

/** The fields TCG Fulfill Order prints one per row, in its two columns. */
export const ORDER_ADDRESS_FIELDS = [
  [
    ["firstName", "First Name"],
    ["lastName", "Last Name"],
    ["addressLine1", "Address Line 1"],
    ["addressLine2", "Address Line 2"],
    ["phoneNumber", "Phone"],
  ],
  [
    ["locality", "City"],
    ["region", "State/Province"],
    ["postalCode", "ZIP Code"],
    ["country", "Country"],
  ],
] as const;

/** One string field of a stored order address, or "": the jsonb may predate the schema. */
export function orderAddressField(address: unknown, key: string): string {
  if (typeof address !== "object" || address === null || Array.isArray(address)) return "";
  const value = (address as Record<string, unknown>)[key];
  return typeof value === "string" ? value.trim() : "";
}
